import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {AreaChart} from '@mantine/charts';
import {useState} from 'react';
import type {AssistantUsageSeriesRowResponse} from 'src/api/ProactApiClient';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {formatUtcShortDate} from 'src/shared/utils/formatters';

export interface AssistantUsageChartProps {
  series: AssistantUsageSeriesRowResponse[] | undefined;
  isLoading: boolean;
  isError: boolean;
  showCost?: boolean;
}

type ChartMode = 'tokens' | 'cost';

export const AssistantUsageChart = ({
  series,
  isLoading,
  isError,
  showCost = true,
}: AssistantUsageChartProps) => {
  const [mode, setMode] = useState<ChartMode>('tokens');

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">
          Failed to load usage data.
        </Text>
      </Center>
    );
  }

  if (!series || series.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">
          No usage recorded in this period.
        </Text>
      </Center>
    );
  }

  const data = series.map(row => ({
    date: formatUtcShortDate(row.date),
    Input: row.tokens.inputTokens,
    Output: row.tokens.outputTokens,
    'Cache Read': row.tokens.cacheReadTokens,
    'Cache Create': row.tokens.cacheCreationTokens,
    Cost: row.tokens.estimatedCostUsd,
  }));

  const showCostChart = showCost && mode === 'cost';

  return (
    <Box>
      {showCost && (
        <Group justify="flex-end" mb="sm">
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={value => setMode(value as ChartMode)}
            data={[
              {label: 'Tokens', value: 'tokens'},
              {label: 'Cost', value: 'cost'},
            ]}
          />
        </Group>
      )}
      {!showCostChart ? (
        <AreaChart
          h={240}
          data={data}
          dataKey="date"
          type="stacked"
          series={[
            {name: 'Input', color: 'blue.6'},
            {name: 'Output', color: 'teal.6'},
            {name: 'Cache Read', color: 'grape.6'},
            {name: 'Cache Create', color: 'orange.6'},
          ]}
          curveType="linear"
          withLegend
          withDots={false}
        />
      ) : (
        <AreaChart
          h={240}
          data={data}
          dataKey="date"
          series={[{name: 'Cost', color: 'green.6'}]}
          valueFormatter={value => `$${value.toFixed(2)}`}
          curveType="linear"
          withDots={false}
        />
      )}
    </Box>
  );
};
